import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { EMAIL_REGEX, LOCALE_LANGUAGE_MAPPING } from '@constants/constants';
import { SignupLayout } from '../../layouts';
import CustomInputField from '../FundingCalculator/components/CustomInputField';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  QueryParams,
  RegisterUserApiMap,
  RegisterUserPayload,
  useRegisterUserMutation,
} from '@api/register';
import { setToastMessage } from '@stores/App/slice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomSelect from '../FundingCalculator/components/CustomSelectField';
import { TOAST_TYPES } from '@constants/constants';
import { trackGAEvent, trackLambdaEvent } from '@utils/analytics';
import { getUtmParams, recordSignUp } from '@services/activityLogger';
import { Trans, useTranslation } from 'react-i18next';
import { InfiniteSearchSelect } from '@components';
import useGetCountriesList from '@hooks/useGetCountriesList';
import { SIGNUP_VARIANT_CONTENT } from './constants';
import { getVariant } from './utils';

const SignupPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t: translation, i18n } = useTranslation();
  const [signupVariant] = useState(() => getVariant());
  const variantContent = SIGNUP_VARIANT_CONTENT[signupVariant];

  const [registerUserApi, { isLoading: isCreatingUser }] = useRegisterUserMutation();
  // Form State
  const [name, setName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [companyHQ, setCompanyHQ] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [avgMonthlyMarketingSpend, setAvgMonthlyMarketingSpend] = useState<string>('');
  const [authorityConsentAccepted, setAuthorityConsentAccepted] = useState(false);
  const [termsOfServiceAccepted, setTermsOfServiceAccepted] = useState(false);
  const [newsletterOptIn, setnewsletterOptIn] = useState(false);
  const [attributedChannel, setAttributedChannel] = useState<string>('');
  const [userDomainAlreadyExists, setUserDomainAlreadyExists] = useState(false);
  const [utmSource, setUtmSource] = useState<string>('Organic_Traffic');
  const [queryParams, setQueryParams] = useState<QueryParams[]>([]);
  const [isUserCreated, setIsUserCreated] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Error State
  const [errorState, setErrorState] = useState({
    name: false,
    companyName: false,
    companyHQ: false,
    email: false,
    avgMonthlyMarketingSpend: false,
    authorityConsentAccepted: false,
    termsOfServiceAccepted: false,
    newsletterOptIn: false,
  });

  const [touched, setTouched] = useState({
    name: false,
    companyName: false,
    companyHQ: false,
    email: false,
    avgMonthlyMarketingSpend: false,
    authorityConsentAccepted: false,
    termsOfServiceAccepted: false,
    newsletterOptIn: false,
  });
  const { countries, onSearchChange } = useGetCountriesList();

  // Capture UTM source parameter on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmSourceParam = urlParams.get('utm_source');
    if (utmSourceParam) {
      setUtmSource(utmSourceParam);
    }
    const queryParamsArray = Array.from(urlParams.entries()).map(([key, value]) => ({
      key,
      value,
    }));
    setQueryParams(queryParamsArray);
  }, []);

  useEffect(() => {
    validateForm();
  }, [
    email,
    companyName,
    name,
    avgMonthlyMarketingSpend,
    authorityConsentAccepted,
    termsOfServiceAccepted,
  ]);

  const ATTRIBUTED_CHANNEL_OPTIONS_WITH_TRANSLATION = [
    {
      value: 'PvX Team',
      label: translation('signup.attributed_channel_options.pvx_team'),
    },
    {
      value: 'Conference',
      label: translation('signup.attributed_channel_options.conference'),
    },
    {
      value: 'Investor',
      label: translation('signup.attributed_channel_options.investor'),
    },
    {
      value: 'Founder',
      label: translation('signup.attributed_channel_options.founder'),
    },
    {
      value: 'Advertisement',
      label: translation('signup.attributed_channel_options.advertisement'),
    },
    {
      value: 'Other',
      label: translation('signup.attributed_channel_options.other'),
    },
  ];

  const MONTHLY_MARKETING_SPENDS_OPTIONS_WITH_TRANSLATION = [
    {
      value: 'Under $100,000',
      label: translation('signup.monthly_marketing_spends_options.under_100k'),
    },
    {
      value: '$100,000 - $250,000',
      label: translation('signup.monthly_marketing_spends_options.100k_250k'),
    },
    {
      value: '$250,000 - $500,000',
      label: translation('signup.monthly_marketing_spends_options.250k_500k'),
    },
    {
      value: '$500,000 - $1,000,000',
      label: translation('signup.monthly_marketing_spends_options.500k_1m'),
    },
    {
      value: '> $1,000,000',
      label: translation('signup.monthly_marketing_spends_options.over_1m'),
    },
  ];

  const handleAttributedChannelChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setAttributedChannel(event.target.value);
  };

  const validateForm = (): boolean => {
    const isFieldEmpty = (field: string) => field.trim().length === 0;

    const newErrorState = {
      ...errorState,
      name: isFieldEmpty(name),
      companyName: isFieldEmpty(companyName),
      email: isFieldEmpty(email) || !EMAIL_REGEX.test(email),
      avgMonthlyMarketingSpend: isFieldEmpty(avgMonthlyMarketingSpend),
      authorityConsentAccepted: !authorityConsentAccepted,
      termsOfServiceAccepted: !termsOfServiceAccepted,
    };

    setErrorState(newErrorState);
    const isValid = !Object.values(newErrorState).some(Boolean);
    setIsFormValid(isValid);

    return isValid;
  };

  const registerUser = async () => {
    const isFormValid = validateForm();
    if (!isFormValid) return;

    const payload: RegisterUserPayload = {
      name: name,
      company: companyName,
      companyHQ: companyHQ || undefined,
      email: email,
      attributedChannel: attributedChannel || 'Website', // NOTE: Website is the fallback value.
      monthlyMarketingSpend: avgMonthlyMarketingSpend,
      dealSource: utmSource,
      userPreference: {
        language: LOCALE_LANGUAGE_MAPPING[i18n.language],
      },
      authorityConsentAccepted: authorityConsentAccepted,
      termsOfServiceAccepted: termsOfServiceAccepted,
      newsletterOptIn: newsletterOptIn,
      queryParams: queryParams,
    };

    try {
      const response: RegisterUserApiMap = await registerUserApi(payload).unwrap();

      if (response?.data) {
        setIsUserCreated(true);

        // No email or name: GA and GTM must not receive PII
        trackLambdaEvent('create_account', {
          signup_source: queryParams.some(
            ({ key, value }) => key === 'signup_source' && value === 'website',
          )
            ? 'website'
            : 'dashboard',
          company: payload.company,
          company_hq: payload.companyHQ,
          monthly_marketing_spend: payload.monthlyMarketingSpend,
          attributed_channel: payload.attributedChannel,
          deal_source: payload.dealSource,
          language: payload.userPreference.language,
          newsletter_opt_in: payload.newsletterOptIn,
          ...getUtmParams(window.location.search),
        });

        // Sent to the activity logger at the user's first login with this email
        recordSignUp({
          email,
          hq: companyHQ || null,
          referrer: utmSource,
          utm: getUtmParams(window.location.search),
        });
      }
    } catch (error: any) {
      const { data } = error;
      if (data) {
        const { code, message } = data;

        switch (code) {
          case 4033:
            setUserDomainAlreadyExists(true);
            break;
          default:
            dispatch(
              setToastMessage({
                type: TOAST_TYPES.ERROR,
                message: message || translation('signup.error.user_registration_error'),
              }),
            );
            break;
        }
      } else {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            message: error?.error?.message || translation('signup.error.user_registration_error'),
          }),
        );
      }
    }
  };

  return (
    <SignupLayout variant={signupVariant}>
      <Box
        sx={{
          paddingTop: '2rem',
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)',
          gap: '1rem',
        }}
      >
        <CustomInputField
          label={translation('signup.full_name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched({ ...touched, name: true })}
          error={errorState.name && touched.name}
          disabled={isUserCreated}
        />
        <CustomInputField
          label={translation('signup.work_email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched({ ...touched, email: true })}
          error={errorState.email && touched.email}
          disabled={isUserCreated}
        />
        <CustomInputField
          label={translation('signup.company_name')}
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          onBlur={() => setTouched({ ...touched, companyName: true })}
          error={errorState.companyName && touched.companyName}
        />
        <Box
          sx={{
            border: '1px solid #989CA24D',
            borderRadius: '20px',
            backgroundColor: 'white',
            paddingTop: '22px',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: '12px',
              fontWeight: 700,
              color: '#162936',
              paddingLeft: '22px',
            }}
          >
            {translation('signup.company_hq')}
          </Typography>
          <InfiniteSearchSelect
            placeholder={''}
            options={countries}
            hasNextPage={false}
            isFetching={false}
            fetchNextPage={() => {}}
            onSelect={(option) => {
              setCompanyHQ(option.value);
              setTouched({ ...touched, companyHQ: true });
            }}
            isLoading={false}
            isError={false}
            errorText="Error loading countries"
            noOptionsText="No countries found"
            searchEnabled={true}
            onSearchChange={onSearchChange}
            selectedValue={
              countries.find((country) => country.value === companyHQ) || { label: '', value: '' }
            }
            selectStyles={{
              border: 'none',
              paddingLeft: '10px',
            }}
            menuStyles={{
              paddingTop: '8px',
              top: 60,
              borderRadius: '4px',
              border: 'none',
              boxShadow: '0px 4px 12px 0px #00000040',
              maxHeight: '300px',
            }}
            menuItemStyles={{
              fontSize: '15px',
              color: '#162936',
              fontWeight: 550,
              lineHeight: '18px',
              letterSpacing: '-0.6px',
            }}
          />
        </Box>
        <CustomSelect
          label={translation('signup.monthly_marketing_spends')}
          value={avgMonthlyMarketingSpend}
          onChange={(e) => setAvgMonthlyMarketingSpend(e.target.value)}
          onBlur={() => setTouched({ ...touched, avgMonthlyMarketingSpend: true })}
          options={MONTHLY_MARKETING_SPENDS_OPTIONS_WITH_TRANSLATION}
          error={errorState.avgMonthlyMarketingSpend && touched.avgMonthlyMarketingSpend}
          disabled={isUserCreated}
        />
        <CustomSelect
          label={translation('signup.how_did_you_hear')}
          value={attributedChannel}
          onChange={handleAttributedChannelChange}
          options={ATTRIBUTED_CHANNEL_OPTIONS_WITH_TRANSLATION}
          error={false}
          disabled={isUserCreated}
        />
      </Box>
      <Box
        sx={{
          marginTop: '2rem',
          backgroundColor: '#162936',
          padding: '1.5rem',
          borderRadius: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        {isUserCreated || userDomainAlreadyExists ? (
          <>
            <Stack direction={'row'} alignItems={'center'} gap={'1.25rem'}>
              <Box
                borderRadius={'50%'}
                minHeight={66}
                minWidth={66}
                sx={{
                  backgroundColor: '#2E4A4F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isUserCreated ? (
                  <CheckIcon sx={{ color: '#7FBCAD' }} />
                ) : (
                  <ErrorOutlineIcon sx={{ color: '#7FBCAD' }} />
                )}
              </Box>

              <Stack>
                <Typography
                  fontSize={isMobile ? '1.25rem' : '1.375rem'}
                  lineHeight={'1.875rem'}
                  fontWeight={700}
                  color={'#F6F8F9'}
                >
                  {isUserCreated
                    ? translation('signup.account_created')
                    : translation('signup.already_registered', { name })}
                </Typography>
                <Typography
                  fontSize={isMobile ? '0.75rem' : '1rem'}
                  lineHeight={'1.375rem'}
                  fontWeight={700}
                  color={'#B4B4B4'}
                >
                  {isUserCreated
                    ? translation('signup.please_sign_in')
                    : translation('signup.sign_in_using', {
                        maskedEmail: `${email[0]}${Array(email.split('@')[0].length - 1)
                          .fill('*')
                          .join('')}@${email.split('@')[1]}`,
                      })}
                </Typography>
              </Stack>
            </Stack>

            <Button
              variant="contained"
              disableElevation
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={() => {
                trackGAEvent({
                  action: 'sign_in',
                  category: 'onboarding',
                });
                navigate('/auth');
              }}
              sx={{
                width: isMobile ? '100%' : '315px',
                maxWidth: '315px',
                paddingTop: '1rem',
                paddingBottom: '1rem',
                borderRadius: '1.5rem',
                fontSize: '1.375rem',
                fontWeight: 700,
                lineHeight: '1.875rem',
                textTransform: 'none',
              }}
            >
              {translation('signup.sign_in')}
            </Button>
          </>
        ) : (
          <>
            <Typography
              fontSize={'1rem'}
              fontWeight={700}
              lineHeight={'1.375rem'}
              color={'#F6F8F9'}
            >
              {translation(variantContent.ctaHeadingKey)}
            </Typography>
            <Button
              variant="contained"
              onClick={registerUser}
              disabled={isCreatingUser || !isFormValid}
              disableElevation
              disableFocusRipple
              disableRipple
              disableTouchRipple
              sx={{
                width: isMobile ? '100%' : '315px',
                maxWidth: '315px',
                paddingTop: '1rem',
                paddingBottom: '1rem',
                borderRadius: '1.5rem',
                fontSize: '1.375rem',
                fontWeight: 700,
                lineHeight: '1.875rem',
                textTransform: 'none',
                ':disabled': {
                  backgroundColor: '#2E4A4F',
                },
              }}
            >
              {isCreatingUser ? (
                <>
                  <CircularProgress
                    size={'1.375rem'}
                    sx={{
                      marginRight: '1rem',
                    }}
                  />
                  <Typography
                    fontSize={'1.375rem'}
                    lineHeight={'1.875rem'}
                    fontWeight={700}
                    color={'#F6F8F9'}
                  >
                    {translation('signup.creating_account')}
                  </Typography>
                </>
              ) : (
                <>{translation(variantContent.ctaButtonKey)}</>
              )}
            </Button>{' '}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '90%',
                fontSize: '12px',
                color: false ? '#F6F8F9' : '#E4E3DBC2',
                lineHeight: '16px',
                fontWeight: 500,
              }}
            >
              <Checkbox
                value={newsletterOptIn}
                onChange={(e: any) => setnewsletterOptIn(e.target.checked)}
                sx={{
                  padding: '0px',
                  color: '#2F736E',
                  '&.Mui-checked': {
                    color: '#2F736E',
                  },
                }}
              />
              <Typography
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
                color={'#E4E3DBC2'}
              >
                {translation('signup.marketing_communications_opt_in')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: '10px',
                width: '90%',
                fontSize: '12px',
                color: false ? '#F6F8F9' : '#F6F8F999',
                lineHeight: '16px',
                fontWeight: 500,
              }}
            >
              <Checkbox
                value={authorityConsentAccepted}
                onChange={(e: any) => setAuthorityConsentAccepted(e.target.checked)}
                sx={{
                  padding: '0px',
                  color: '#2F736E',
                  '&.Mui-checked': {
                    color: '#2F736E',
                  },
                }}
              />
              <Typography
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
                color={'#E4E3DBC2'}
              >
                {translation('signup.data_access_terms')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '90%',
                fontSize: '12px',
                color: false ? '#F6F8F9' : '#E4E3DBC2',
                lineHeight: '16px',
                fontWeight: 500,
              }}
            >
              <Checkbox
                value={termsOfServiceAccepted}
                onChange={(e: any) => setTermsOfServiceAccepted(e.target.checked)}
                sx={{
                  padding: '0px',
                  color: '#2F736E',
                  '&.Mui-checked': {
                    color: '#2F736E',
                  },
                }}
              />

              <Typography
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
                color={'#E4E3DBC2'}
              >
                <Trans
                  i18nKey={'signup.terms_and_privacy'}
                  components={{
                    1: (
                      <a
                        href="https://pvxpartners.com/terms-of-use"
                        aria-label="Terms of Use"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#E4E3DBC2',
                          textDecoration: 'underline',
                        }}
                      />
                    ),
                    2: (
                      <a
                        href="https://pvxpartners.com/privacy-policy"
                        aria-label="Privacy Policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#E4E3DBC2',
                          textDecoration: 'underline',
                        }}
                      />
                    ),
                  }}
                />
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </SignupLayout>
  );
};

export default SignupPage;
